import React from 'react';
// Ícones adicionados: FileText (projetos), ListChecks (pendências), Trash2 (Limpar) e AtSign (menção)
import { Bell, UserCheck, Briefcase, X as IconX, FileText, ListChecks, Trash2, Loader, AtSign } from 'lucide-react';

/**
 * Define o ícone apropriado com base no tipo de notificação.
 * @param {string} type - O tipo da notificação.
 * @returns {React.Element} O componente de ícone.
 */
const getIconForType = (type) => {
    switch (type) {
        case 'newUser':
            return <UserCheck className="h-5 w-5 text-green-500" />;
        case 'pendencyAssigned':
            return <Briefcase className="h-5 w-5 text-blue-500" />;
        case 'projectUpdate':
            return <FileText className="h-5 w-5 text-purple-500" />;
        case 'projectMention':
            return <AtSign className="h-5 w-5 text-indigo-500" />;
        case 'pendencyUpdate':
            return <ListChecks className="h-5 w-5 text-orange-500" />;
        default:
            return <Bell className="h-5 w-5 text-slate-500" />;
    }
};

/**
 * Modal para exibir a lista de notificações do usuário.
 * @param {object} props - Propriedades do componente.
 * @param {boolean} props.isOpen - Controla a visibilidade do modal.
 * @param {function} props.onClose - Função para fechar o modal.
 * @param {Array} props.notifications - A lista de notificações a serem exibidas.
 * @param {function} props.onNotificationClick - Função a ser executada quando uma notificação é clicada.
 * @param {function} props.onClearAll - Função para limpar todas as notificações.
 * @param {boolean} props.isProcessing - Indica se uma operação (como limpar) está em andamento.
 * @returns {React.Component|null} O componente do modal de notificações ou nulo se não estiver aberto.
 */
const NotificationsModal = ({ isOpen, onClose, notifications, onNotificationClick, onClearAll, isProcessing, theme }) => {
    if (!isOpen) return null;

    const hasNotifications = notifications.length > 0;

    /**
     * @description Limpa todas as notificações e fecha o modal após um atraso de 2 segundos.
     */
    const handleClearAndClose = () => {
        onClearAll();
        setTimeout(() => {
            onClose();
        }, 2000);
    };

    return (
        // Overlay de fundo
        <div className="fixed inset-0 bg-black/60 z-[1050] flex justify-center items-start pt-20" onClick={onClose}>
            {/* Conteúdo do Modal */}
            <div 
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col" 
                onClick={e => e.stopPropagation()}
            >
                {/* Cabeçalho do Modal */}
                <header className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <h2 className="text-xl font-bold flex items-center"><Bell className="mr-3"/>Notificações</h2>
                    <button onClick={onClose} aria-label="Fechar" title="Fechar" className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"><IconX /></button>
                </header>
                
                {/* Corpo do Modal com a lista de notificações */}
                <div className="flex-grow overflow-y-auto">
                    {hasNotifications ? (
                        <div className="divide-y divide-slate-200 dark:divide-slate-700">
                            {notifications.map(notif => (
                                <div 
                                    key={notif.id} 
                                    onClick={() => onNotificationClick(notif)}
                                    className="p-4 flex items-start gap-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                                >
                                    <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-full">
                                        {getIconForType(notif.type)}
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-800 dark:text-slate-100">{notif.message}</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            {new Date(notif.createdAt).toLocaleString('pt-BR')}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center p-10 text-slate-500 dark:text-slate-400">
                            <Bell className="mx-auto h-12 w-12 mb-4"/>
                            <p>Nenhuma notificação nova.</p>
                        </div>
                    )}
                </div>

                {/* --- NOVO RODAPÉ COM O BOTÃO DE LIMPAR --- */}
                {hasNotifications && (
                    <footer className="p-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                        <button 
                            onClick={handleClearAndClose}
                            disabled={isProcessing}
                            className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full transition-colors disabled:opacity-50"
                            title="Limpar Todas as Notificações"
                        >
                            {isProcessing ? (
                                <Loader className="h-5 w-5 animate-spin" />
                            ) : (
                                <Trash2 className="h-5 w-5" />
                            )}
                        </button>
                    </footer>
                )}
            </div>
        </div>
    );
};

export default NotificationsModal;
